import { type ApexOptions } from 'apexcharts'
import { format, subDays, subHours } from 'date-fns'
import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import type { ApplicationDate } from '../Dashboard.types'

const height = 120

const now = new Date()

const dayFormat = 'dd MMM yyyy'
const hourFormat = 'dd MMM yyyy HH:00'

const getRanges = (range: string, days?: number) => {
  switch (range) {
    case '7d': {
      return Array.from({ length: 7 }, (_, i) => format(subDays(now, i), dayFormat)).reverse()
    }
    case '30d': {
      return Array.from({ length: 30 }, (_, i) => format(subDays(now, i), dayFormat)).reverse()
    }
    case '24h': {
      return Array.from({ length: 24 }, (_, i) => format(subHours(now, i), hourFormat)).reverse()
    }
    case 'custom': {
      return Array.from({ length: days || 7 }, (_, i) => format(subDays(now, i), dayFormat)).reverse()
    }
    default: {
      return []
    }
  }
}

const options = (range: string | null): ApexOptions => ({
  chart: {
    type: 'line',
    height,
    width: '100%',
    stacked: true,
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
    sparkline: {
      enabled: false,
    },
    dropShadow: {
      enabled: true,
      top: 4,
      left: 0,
      blur: 8,
      opacity: 1,
      color: '#9C4EFF',
    },
    animations: {
      easing: 'easeinout',
      animateGradually: {
        enabled: false,
      },
    },
  },
  grid: {
    show: false,
  },
  stroke: {
    width: 2,
    curve: 'smooth',
    colors: ['#9C4EFF'], // kolor linii
  },
  tooltip: {
    x: {
      format: range?.endsWith('h') ? hourFormat : dayFormat, // lub inny dowolny format, np. 'dd/MM/yyyy'
    },
    y: {
      formatter: (val: number) => Math.round(val).toString(),
    },
  },
  legend: {
    show: false,
  },
  yaxis: {
    axisTicks: {
      show: false,
    },
    labels: {
      show: false,
    },
    axisBorder: {
      show: false,
    },
    min: -2,
  },
  fill: {
    opacity: 1,
  },
  colors: ['#9E77ED'], // kolor główny
})

export const RangeData = ({ range, data }: { range: string | null; data: ApplicationDate[] }) => {
  const minDate = Math.min(...data.map(el => new Date(el.createdAt).getTime()))

  const diff = now.getTime() - minDate
  const daysDiff = Math.ceil(diff / 1000 / 60 / 60 / 24) + 3

  const formattedRanges = getRanges(range || 'custom', daysDiff)

  const { xaxis, series } = useMemo(() => {
    const xaxis: ApexOptions['xaxis'] = {
      type: 'category',
      axisTicks: {
        show: false,
        height: 0,
      },
      categories: formattedRanges,
      labels: {
        show: false,
      },
      tooltip: {
        enabled: false,
      },
      axisBorder: {
        show: false,
      },
    }

    const series: ApexOptions['series'] = [
      {
        name: 'Created',
        color: '#9E77ED',
        data: formattedRanges.map(
          date =>
            data.filter(
              item =>
                format(new Date(item.createdAt.slice(0, 10)), range?.endsWith('h') ? hourFormat : dayFormat) === date
            ).length
        ),
      },
    ]

    return { xaxis, series }
  }, [range, data, formattedRanges])

  return (
    <div id="chart">
      <ReactApexChart options={{ ...options(range), xaxis }} series={series} type="line" height={height} />
    </div>
  )
}
